/* ============================================================================
 * Movatic WMS — Frontend MES API | CesarFilho 932048016
 * Arquivo: src/api/mes.ts
 * ----------------------------------------------------------------------------
 * Cliente para /api/mes/... com normalização de respostas paginadas (DRF).
 * - Se a API devolver {results:[...]}, retorna apenas o array.
 * - Caso venha array, retorna como está.
 * - Auth: lê token do localStorage (chaves comuns).
 * - Corrigido: montagem de HeadersInit (sem erro de tipagem no fetch).
 * ========================================================================== */

export type Id = number | string;

export interface WorkCenter {
    id: number;
    company: number;
    code: string;
    name: string;
    kind: "MANUAL" | "MACHINE" | string;
    capacity: number;
    calendar?: string;
}

export interface POMaterial {
    id: number;
    component: number;
    component_code?: string;
    qty_req: string | number;
    uom: string;
    is_main: boolean;
}

export interface ProdOrder {
    id: number;
    company: number;
    ref_code: string;
    product: number;
    product_code?: string;
    variant?: string;
    qty: string | number;
    uom: string;
    lot_code?: string;
    status:
    | "DRAFT" | "PLANNED" | "ALLOCATED" | "PICKED"
    | "IN_PRODUCTION" | "PAUSED" | "DONE" | "PUTAWAY" | "CLOSED" | "CANCELED";
    planned_date?: string | null;
    workcenter?: number | null;
    routing?: number | null;
    process_code?: string;
    materials?: POMaterial[];
}

const API_BASE =
    (import.meta as any).env?.VITE_API_BASE?.replace(/\/$/, "") ??
    (typeof window !== "undefined" ? `${window.location.origin}/api` : "/api");

function getToken(): string | null {
    return (
        localStorage.getItem("access") ||
        localStorage.getItem("token") ||
        localStorage.getItem("auth.token") ||
        localStorage.getItem("movatic_token")
    );
}

function buildHeaders(extra?: HeadersInit): HeadersInit {
    // Começamos com um objeto plano e **convertimos** qualquer Headers/array depois.
    const base: Record<string, string> = { "Content-Type": "application/json" };
    const t = getToken();
    if (t) base["Authorization"] = `Bearer ${t}`;

    if (!extra) return base;

    if (extra instanceof Headers) {
        extra.forEach((v, k) => { base[k] = v; });
        return base;
    }
    if (Array.isArray(extra)) {
        extra.forEach(([k, v]) => { base[k] = v as string; });
        return base;
    }
    // Record<string, string>
    Object.assign(base, extra as Record<string, string>);
    return base;
}

async function request<T = any>(path: string, init?: RequestInit): Promise<T> {
    const resp = await fetch(`${API_BASE}${path}`, {
        ...init,
        headers: buildHeaders(init?.headers),
    });
    if (!resp.ok) {
        let detail: any = await resp.text().catch(() => "");
        try { detail = JSON.parse(detail); } catch { /* ignore */ }
        const msg = (detail && (detail.detail || detail.message)) || `${resp.status} ${resp.statusText}`;
        throw new Error(msg);
    }
    if (resp.status === 204) return undefined as any;
    return (await resp.json()) as T;
}

/** Normaliza resposta de lista: array direto ou DRF {results:[...]}. */
function toArray<T = any>(data: any): T[] {
    if (Array.isArray(data)) return data as T[];
    if (data && Array.isArray(data.results)) return data.results as T[];
    return [];
}

/** Helper para GET de listas com normalização. */
async function list<T = any>(path: string, params: Record<string, any> = {}): Promise<T[]> {
    const qs = new URLSearchParams(params as any).toString();
    const data = await request<any>(`${path}${qs ? `?${qs}` : ""}`);
    return toArray<T>(data);
}

export const MES = {
    // Work Centers
    listWorkCenters: (params: Record<string, any> = {}) => list<WorkCenter>("/mes/work-centers/", params),
    createWorkCenter: (body: Partial<WorkCenter>) =>
        request<WorkCenter>("/mes/work-centers/", { method: "POST", body: JSON.stringify(body) }),

    // Orders
    listOrders: (params: Record<string, any> = {}) => list<ProdOrder>("/mes/orders/", params),
    getOrder: (id: Id) => request<ProdOrder>(`/mes/orders/${id}/`),
    createOrder: (body: Partial<ProdOrder>) =>
        request<ProdOrder>("/mes/orders/", { method: "POST", body: JSON.stringify(body) }),

    // Actions
    allocate: (id: Id) => request(`/mes/orders/${id}/allocate/`, { method: "POST" }),
    start: (id: Id) => request(`/mes/orders/${id}/start/`, { method: "POST" }),
    pause: (id: Id) => request(`/mes/orders/${id}/pause/`, { method: "POST" }),
    resume: (id: Id) => request(`/mes/orders/${id}/resume/`, { method: "POST" }),
    finish: (id: Id) => request(`/mes/orders/${id}/finish/`, { method: "POST" }),

    consume: (id: Id, payload: { component: Id; qty: number; uom?: string; lot?: string; location?: Id }) =>
        request(`/mes/orders/${id}/consume/`, { method: "POST", body: JSON.stringify(payload) }),

    report: (id: Id, payload: { qty: number; uom?: string; lot?: string; sscc?: string; location?: Id }) =>
        request(`/mes/orders/${id}/report/`, { method: "POST", body: JSON.stringify(payload) }),
};

export default MES;
